import { useCallback, useEffect, useState } from "react"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { FilterDropdown } from "../../shared/ui/FilterDropdown"
import { CopyableId } from "../../shared/ui/CopyableId"
import {
  listSubscriptionsApi,
  SubscriptionItem,
  SubscriptionStatus,
  SubscriptionTargetType,
} from "../../features/payments/api/payments.api"
import {
  SparklesIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  BuildingOffice2Icon,
  BriefcaseIcon,
} from "@heroicons/react/16/solid"

const LIMIT = 20

const STATUS_CONFIG: Record<SubscriptionStatus, { label: string; className: string; dot: string }> = {
  active:   { label: "Активна",  className: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200", dot: "bg-emerald-400" },
  expired:  { label: "Истекла",  className: "bg-gray-100 text-gray-500 ring-1 ring-gray-200",        dot: "bg-gray-400" },
  canceled: { label: "Отменена", className: "bg-red-50 text-red-700 ring-1 ring-red-200",             dot: "bg-red-400" },
}

const TARGET_CONFIG: Record<SubscriptionTargetType, { label: string; icon: React.ReactNode }> = {
  china_access: { label: "Доступ к Китаю",     icon: <BuildingOffice2Icon className="w-3.5 h-3.5" /> },
  job_access:   { label: "Доступ к вакансиям", icon: <BriefcaseIcon className="w-3.5 h-3.5" /> },
}

const StatusBadge = ({ status }: { status: SubscriptionStatus }) => {
  const cfg = STATUS_CONFIG[status] ?? { label: status, className: "bg-gray-100 text-gray-500" }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  )
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric" })

export const SubscriptionsPage = () => {
  const [items, setItems] = useState<SubscriptionItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)

  const [filterStatus, setFilterStatus] = useState<SubscriptionStatus | "">("")
  const [filterTarget, setFilterTarget] = useState<SubscriptionTargetType | "">("")
  const [userId, setUserId] = useState("")
  const [pendingUserId, setPendingUserId] = useState("")

  const load = useCallback(async () => {
    setLoading(true)
    const offset = (page - 1) * LIMIT
    const res = await listSubscriptionsApi({ limit: LIMIT, offset, status: filterStatus, target_type: filterTarget, user_id: userId || undefined })
    setItems(res.data.items)
    setTotal(res.data.total)
    setLoading(false)
  }, [page, filterStatus, filterTarget, userId])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(1) }, [filterStatus, filterTarget, userId])

  const applyUserFilter = () => setUserId(pendingUserId.trim())

  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <DashboardLayout title="Подписки">
      <div className="bg-white rounded-xl border overflow-hidden">

        {/* ── Filters ── */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-b bg-gray-50/60">
          <FunnelIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />

          <FilterDropdown
            label="Статус"
            value={filterStatus}
            onChange={setFilterStatus}
            options={(Object.keys(STATUS_CONFIG) as SubscriptionStatus[]).map((s) => ({ value: s, label: STATUS_CONFIG[s].label, dot: STATUS_CONFIG[s].dot }))}
          />

          <FilterDropdown
            label="Тип"
            value={filterTarget}
            onChange={setFilterTarget}
            options={(Object.keys(TARGET_CONFIG) as SubscriptionTargetType[]).map((t) => ({ value: t, label: TARGET_CONFIG[t].label }))}
          />

          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 bg-white">
            <MagnifyingGlassIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input
              type="text"
              placeholder="ID пользователя…"
              value={pendingUserId}
              onChange={(e) => setPendingUserId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyUserFilter()}
              className="w-36 text-xs outline-none font-mono"
            />
          </div>
          <button onClick={applyUserFilter} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors">
            Найти
          </button>

          {(filterStatus || filterTarget || userId) && (
            <button
              type="button"
              onClick={() => { setFilterStatus(""); setFilterTarget(""); setUserId(""); setPendingUserId("") }}
              className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
            >
              Сбросить фильтры
            </button>
          )}

          <span className="ml-auto text-xs text-gray-400">{!loading && `${total} подписок`}</span>
        </div>

        {/* ── Table ── */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/40">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID пользователя</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Тип</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">План</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Период</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {[1, 2, 3, 4, 5].map((j) => (
                      <td key={j} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse w-24" /></td>
                    ))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-16 text-center">
                    <SparklesIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Подписок не найдено</p>
                  </td>
                </tr>
              ) : (
                items.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-3.5"><CopyableId value={s.user_id} /></td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                        {TARGET_CONFIG[s.target_type]?.icon}
                        {TARGET_CONFIG[s.target_type]?.label ?? s.target_type}
                      </span>
                    </td>
                    <td className="px-5 py-3.5"><span className="font-mono text-xs text-gray-500">{s.plan_id.slice(0, 10)}…</span></td>
                    <td className="px-5 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                      {formatDate(s.current_period_start)} → {formatDate(s.current_period_end)}
                    </td>
                    <td className="px-5 py-3.5"><StatusBadge status={s.status} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
            <span className="text-xs text-gray-400">
              Страница <span className="font-medium text-gray-600">{page}</span> из <span className="font-medium text-gray-600">{totalPages}</span>
            </span>
            <div className="flex gap-1.5">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">← Назад</button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Вперёд →</button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}